import { DEFAULT_TRACK_ID, getTrackDefinition, getTrackLength } from "./track";
import type { CourseEventLane, CourseEventPatternIndex, CourseEventPhase, CourseEventState, KartState, RaceState, RaceVariantState } from "./types";
import { getVariantEventLane, normalizeRaceVariantSeed } from "./raceVariants";

export type { CourseEventLane, CourseEventPhase, CourseEventState } from "./types";

export type CourseEventVisualKind =
  | "steam-sweep"
  | "mash-burst"
  | "cargo-swing"
  | "forklift-dash"
  | "tumbleweed-wall"
  | "mesa-rockfall"
  | "cask-roll"
  | "chandelier-drop"
  | "aqueduct-spill"
  | "market-cart"
  | "orbital-drone"
  | "meteor-shower";

/**
 * A course event is a short, lane-sized interruption placed on the authored
 * centreline.  The simulation owns the timing and lane; renderers only need
 * the state below to present the same event the server is applying.
 */
export interface CourseEventDefinition {
  id: string;
  trackId: string;
  patternIndex: CourseEventPatternIndex;
  visualKind: CourseEventVisualKind;
  label: string;
  description: string;
  /** Centre point as a fraction of the main route length. */
  progress: number;
  /** Explicit alias used by callers that describe the point as a trigger. */
  triggerProgress: number;
  /** Explicit alias for presentation/content tooling. */
  eventProgress: number;
  /** Centre point in the track's local distance space. */
  distance: number;
  eventDistance: number;
  /** Point at which the leader starts telegraphing this lap's event. */
  triggerDistance: number;
  laneCenter: number;
  laneWidth: number;
  /** Longitudinal hit radius around the event centre. */
  hitDistance: number;
  /** Alias retained for simple hazard-like consumers. */
  radius: number;
  telegraphDistance: number;
  telegraphDuration: number;
  activeDuration: number;
  cooldownDuration: number;
  /** Multiplier applied once to a kart's velocity on a hit. */
  slowdownFactor: number;
  slowdown: number;
  /** Per-kart immunity window; longer than the active window by design. */
  hitCooldown: number;
  defaultBlockedLane: CourseEventLane;
  lane: CourseEventLane;
}

interface EventSpec {
  trackId: string;
  id: string;
  patternIndex: CourseEventPatternIndex;
  visualKind: CourseEventVisualKind;
  label: string;
  description: string;
  progress: number;
  laneCenter: number;
  laneWidth: number;
  hitDistance: number;
  telegraphDistance: number;
  telegraphDuration: number;
  activeDuration: number;
  cooldownDuration: number;
  slowdownFactor: number;
  hitCooldown: number;
  defaultBlockedLane: CourseEventLane;
}

/*
 * One authored definition per venue.  There is no runtime randomness: lane
 * parity is derived from the lap number, which keeps server, prediction and
 * replay deterministic while still making the three-lap race vary.
 */
const EVENT_SPECS: readonly EventSpec[] = [
  {
    trackId: "malt-mountain",
    id: "malt-steam-sweep",
    patternIndex: 0,
    visualKind: "steam-sweep",
    label: "STEAM VENT SWEEP",
    description: "A brewery steam vent blocks one side of the climb.",
    progress: 0.305,
    laneCenter: 4.5,
    laneWidth: 5.8,
    hitDistance: 6.2,
    telegraphDistance: 46,
    telegraphDuration: 1.25,
    activeDuration: 2.35,
    cooldownDuration: 4.8,
    slowdownFactor: 0.66,
    hitCooldown: 2.8,
    defaultBlockedLane: 1,
  },
  {
    trackId: "malt-mountain",
    id: "malt-mash-burst",
    patternIndex: 1,
    visualKind: "mash-burst",
    label: "MASH TUN BURST",
    description: "A pressure surge throws hot mash and copper lids across one side of the descent.",
    progress: 0.66,
    laneCenter: 4.35,
    laneWidth: 5.4,
    hitDistance: 5.6,
    telegraphDistance: 52,
    telegraphDuration: 1.45,
    activeDuration: 1.95,
    cooldownDuration: 4.6,
    slowdownFactor: 0.7,
    hitCooldown: 2.5,
    defaultBlockedLane: -1,
  },
  {
    trackId: "neon-harbor",
    id: "neon-cargo-crane",
    patternIndex: 0,
    visualKind: "cargo-swing",
    label: "CARGO CRANE SWING",
    description: "A dock crane swings across one lane before the next bend.",
    progress: 0.425,
    laneCenter: 4.5,
    laneWidth: 5.8,
    hitDistance: 6.2,
    telegraphDistance: 48,
    telegraphDuration: 1.3,
    activeDuration: 2.4,
    cooldownDuration: 4.9,
    slowdownFactor: 0.64,
    hitCooldown: 2.9,
    defaultBlockedLane: -1,
  },
  {
    trackId: "neon-harbor",
    id: "neon-forklift-dash",
    patternIndex: 1,
    visualKind: "forklift-dash",
    label: "FORKLIFT DASH",
    description: "A neon yard forklift darts out with a pallet of kegs before the flyover.",
    progress: 0.69,
    laneCenter: 4.35,
    laneWidth: 5.5,
    hitDistance: 5.8,
    telegraphDistance: 54,
    telegraphDuration: 1.5,
    activeDuration: 1.9,
    cooldownDuration: 4.7,
    slowdownFactor: 0.68,
    hitCooldown: 2.55,
    defaultBlockedLane: 1,
  },
  {
    trackId: "dustbowl-draft",
    id: "dustbowl-tumbleweed",
    patternIndex: 0,
    visualKind: "tumbleweed-wall",
    label: "TUMBLEWEED WALL",
    description: "A gust drives a tumbleweed wall into one side of the mesa.",
    progress: 0.565,
    laneCenter: 4.5,
    laneWidth: 5.8,
    hitDistance: 6.4,
    telegraphDistance: 50,
    telegraphDuration: 1.2,
    activeDuration: 2.5,
    cooldownDuration: 5,
    slowdownFactor: 0.68,
    hitCooldown: 3,
    defaultBlockedLane: 1,
  },
  {
    trackId: "dustbowl-draft",
    id: "dustbowl-mesa-rockfall",
    patternIndex: 1,
    visualKind: "mesa-rockfall",
    label: "MESA ROCKFALL",
    description: "Loose sandstone crashes onto one side of the approach to the trestle.",
    progress: 0.42,
    laneCenter: 4.4,
    laneWidth: 5.6,
    hitDistance: 5.9,
    telegraphDistance: 56,
    telegraphDuration: 1.55,
    activeDuration: 2.05,
    cooldownDuration: 4.9,
    slowdownFactor: 0.69,
    hitCooldown: 2.7,
    defaultBlockedLane: -1,
  },
  {
    trackId: "keg-catacombs",
    id: "catacomb-cask-roll",
    patternIndex: 0,
    visualKind: "cask-roll",
    label: "CASK ROLL",
    description: "A loose cellar cask rolls through one of the crypt lanes.",
    progress: 0.355,
    laneCenter: 4.5,
    laneWidth: 5.8,
    hitDistance: 6.1,
    telegraphDistance: 44,
    telegraphDuration: 1.35,
    activeDuration: 2.3,
    cooldownDuration: 4.7,
    slowdownFactor: 0.63,
    hitCooldown: 2.75,
    defaultBlockedLane: -1,
  },
  {
    trackId: "keg-catacombs",
    id: "catacomb-chandelier-drop",
    patternIndex: 1,
    visualKind: "chandelier-drop",
    label: "CHAIN CHANDELIER DROP",
    description: "A vault chandelier crashes down across one crypt lane.",
    progress: 0.69,
    laneCenter: 4.35,
    laneWidth: 5.5,
    hitDistance: 5.7,
    telegraphDistance: 50,
    telegraphDuration: 1.5,
    activeDuration: 2,
    cooldownDuration: 4.75,
    slowdownFactor: 0.66,
    hitCooldown: 2.65,
    defaultBlockedLane: 1,
  },
  {
    trackId: "citrus-courtyard",
    id: "citrus-aqueduct-spill",
    patternIndex: 0,
    visualKind: "aqueduct-spill",
    label: "AQUEDUCT SPILL",
    description: "A burst aqueduct sluice makes one terrace lane treacherous.",
    progress: 0.655,
    laneCenter: 4.5,
    laneWidth: 5.8,
    hitDistance: 6.3,
    telegraphDistance: 49,
    telegraphDuration: 1.25,
    activeDuration: 2.4,
    cooldownDuration: 4.9,
    slowdownFactor: 0.67,
    hitCooldown: 2.9,
    defaultBlockedLane: 1,
  },
  {
    trackId: "citrus-courtyard",
    id: "citrus-market-cart",
    patternIndex: 1,
    visualKind: "market-cart",
    label: "MARKET CART CROSSING",
    description: "A festival cart stacked with oranges rolls across one terrace lane.",
    progress: 0.43,
    laneCenter: 4.4,
    laneWidth: 5.5,
    hitDistance: 5.8,
    telegraphDistance: 53,
    telegraphDuration: 1.45,
    activeDuration: 2.05,
    cooldownDuration: 4.8,
    slowdownFactor: 0.71,
    hitCooldown: 2.7,
    defaultBlockedLane: -1,
  },
  {
    trackId: "moon-tap-one",
    id: "moon-orbit-drone",
    patternIndex: 0,
    visualKind: "orbital-drone",
    label: "ORBITAL DRONE SWEEP",
    description: "An orbital service drone sweeps down one crater lane.",
    progress: 0.535,
    laneCenter: 4.5,
    laneWidth: 5.8,
    hitDistance: 6.5,
    telegraphDistance: 52,
    telegraphDuration: 1.4,
    activeDuration: 2.55,
    cooldownDuration: 5.1,
    slowdownFactor: 0.65,
    hitCooldown: 3.05,
    defaultBlockedLane: -1,
  },
  {
    trackId: "moon-tap-one",
    id: "moon-meteor-shower",
    patternIndex: 1,
    visualKind: "meteor-shower",
    label: "METEOR SHOWER",
    description: "A tight meteor cluster strikes one side of the final crater run.",
    progress: 0.24,
    laneCenter: 4.4,
    laneWidth: 5.6,
    hitDistance: 6,
    telegraphDistance: 58,
    telegraphDuration: 1.6,
    activeDuration: 2.1,
    cooldownDuration: 5,
    slowdownFactor: 0.67,
    hitCooldown: 2.8,
    defaultBlockedLane: 1,
  },
] as const;

const courseEventCatalogInternal: readonly CourseEventDefinition[] = EVENT_SPECS.map((spec) => {
  const trackId = getTrackDefinition(spec.trackId).id;
  const distance = getTrackLength(trackId) * spec.progress;
  return {
    ...spec,
    trackId,
    triggerProgress: spec.progress,
    eventProgress: spec.progress,
    distance,
    eventDistance: distance,
    triggerDistance: Math.max(0, distance - spec.telegraphDistance),
    radius: spec.hitDistance,
    slowdown: spec.slowdownFactor,
    lane: spec.defaultBlockedLane,
  };
});

export const courseEventCatalog: readonly CourseEventDefinition[] = courseEventCatalogInternal;
/** Alias for systems that use the noun "definitions" rather than catalog. */
export const courseEventDefinitions: readonly CourseEventDefinition[] = courseEventCatalog;

const definitionsByTrack = new Map(trackCatalogForEvents().map((trackId) => [trackId, courseEventCatalog.filter((definition) => definition.trackId === trackId)]));
const definitionById = new Map(courseEventCatalog.map((definition) => [definition.id, definition]));

function trackCatalogForEvents(): string[] {
  return [...new Set(courseEventCatalog.map((definition) => definition.trackId))];
}

const PHASE_EPSILON = 1e-9;
const MIN_SLOWDOWN_FACTOR = 0.55;
const MAX_SLOWDOWN_FACTOR = 0.9;

function oppositeLane(lane: CourseEventLane): CourseEventLane {
  return lane === 1 ? -1 : 1;
}

function finiteOr(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.max(minimum, Math.min(maximum, value));
}

function phaseDuration(definition: CourseEventDefinition, phase: CourseEventPhase): number {
  if (phase === "telegraph") return definition.telegraphDuration;
  if (phase === "active") return definition.activeDuration;
  return definition.cooldownDuration;
}

function syncPhaseClock(event: CourseEventState, now: number): void {
  const phaseEnd = finiteOr(event.phaseEndsAt, event.phaseStartedAt);
  const phaseStart = finiteOr(event.phaseStartedAt, now);
  event.phaseTime = clamp(now - phaseStart, 0, Math.max(0, phaseEnd - phaseStart));
  event.phaseRemaining = Math.max(0, phaseEnd - now);
}

function setPhase(event: CourseEventState, phase: CourseEventPhase, startedAt: number, definition: CourseEventDefinition): void {
  event.phase = phase;
  event.phaseStartedAt = startedAt;
  event.phaseEndsAt = startedAt + phaseDuration(definition, phase);
  event.phaseTime = 0;
  event.phaseRemaining = phaseDuration(definition, phase);
}

function blockedLaneForLap(definition: CourseEventDefinition, lap: number, variant?: Pick<RaceVariantState, "index">): CourseEventLane {
  return getVariantEventLane(variant, definition.defaultBlockedLane, lap);
}

function syncLane(event: CourseEventState, definition: CourseEventDefinition, lap: number, variant?: Pick<RaceVariantState, "index">): void {
  const blockedLane = blockedLaneForLap(definition, lap, variant);
  event.blockedLane = blockedLane;
  event.safeLane = oppositeLane(blockedLane);
  event.lane = blockedLane;
}

export function getCourseEventPatternIndex(variant?: Pick<RaceVariantState, "seed">): CourseEventPatternIndex {
  return normalizeRaceVariantSeed(variant?.seed) % 2 === 1 ? 1 : 0;
}

export function getCourseEventDefinitions(trackId = DEFAULT_TRACK_ID): readonly CourseEventDefinition[] {
  const canonicalTrackId = getTrackDefinition(trackId).id;
  return definitionsByTrack.get(canonicalTrackId) ?? [];
}

/** Return the deterministic definition for a track/seed, falling back to pattern zero for old snapshots. */
export function getCourseEventDefinition(trackId = DEFAULT_TRACK_ID, variant?: Pick<RaceVariantState, "seed">): CourseEventDefinition | null {
  const definitions = getCourseEventDefinitions(trackId);
  const patternIndex = getCourseEventPatternIndex(variant);
  return definitions.find((definition) => definition.patternIndex === patternIndex) ?? definitions[0] ?? null;
}

export function getCourseEventDefinitionById(id: string): CourseEventDefinition | undefined {
  return definitionById.get(id);
}

export function createCourseEventState(trackId = DEFAULT_TRACK_ID, variant?: Pick<RaceVariantState, "index" | "seed">): CourseEventState | null {
  const definition = getCourseEventDefinition(trackId, variant);
  if (!definition) return null;
  const blockedLane = blockedLaneForLap(definition, 1, variant);
  return {
    id: definition.id,
    definitionId: definition.id,
    patternIndex: definition.patternIndex,
    label: definition.label,
    name: definition.label,
    trackId: definition.trackId,
    phase: "cooldown",
    lap: 0,
    triggerCount: 0,
    blockedLane,
    safeLane: oppositeLane(blockedLane),
    lane: blockedLane,
    phaseTime: 0,
    phaseRemaining: 0,
    hitCount: 0,
    distance: definition.distance,
    eventDistance: definition.distance,
    triggerDistance: definition.triggerDistance,
    phaseStartedAt: 0,
    phaseEndsAt: 0,
  };
}

function ensureCourseEvent(race: RaceState, definition: CourseEventDefinition): CourseEventState {
  const current = race.courseEvent;
  if (current && current.trackId === definition.trackId && current.id === definition.id) return current;
  const replacement = createCourseEventState(definition.trackId, race.raceVariant);
  if (!replacement) throw new Error(`Course event definition missing for ${definition.trackId}`);
  race.courseEvent = replacement;
  return replacement;
}

function leaderDistance(race: RaceState): number {
  let leader = Number.NEGATIVE_INFINITY;
  for (const kart of race.karts) {
    if (kart.finishedAt !== null || !Number.isFinite(kart.distance)) continue;
    leader = Math.max(leader, kart.distance);
  }
  return leader;
}

function advancePhase(event: CourseEventState, definition: CourseEventDefinition, now: number): void {
  let transitions = 0;
  while (transitions < 3 && now + PHASE_EPSILON >= event.phaseEndsAt) {
    const phase = event.phase;
    if (phase === "cooldown") break;
    const boundary = event.phaseEndsAt;
    if (phase === "telegraph") setPhase(event, "active", boundary, definition);
    else setPhase(event, "cooldown", boundary, definition);
    transitions += 1;
  }
  syncPhaseClock(event, now);
}

function eventCentreDistance(event: CourseEventState, definition: CourseEventDefinition, trackLength: number): number {
  return Math.max(0, event.lap - 1) * trackLength + definition.distance;
}

function beginNextLapEvent(
  race: RaceState,
  event: CourseEventState,
  definition: CourseEventDefinition,
  lap: number,
  now: number,
): void {
  event.lap = lap;
  event.triggerCount += 1;
  syncLane(event, definition, lap, race.raceVariant);
  event.distance = definition.distance;
  event.eventDistance = definition.distance;
  event.triggerDistance = definition.triggerDistance;
  setPhase(event, "telegraph", now, definition);
}

function updateEventState(race: RaceState, event: CourseEventState, definition: CourseEventDefinition): void {
  const now = finiteOr(race.elapsed, 0);
  advancePhase(event, definition, now);
  if (event.phase !== "cooldown") return;
  if (event.lap >= Math.max(1, Math.floor(finiteOr(race.totalLaps, 3)))) return;
  if (now + PHASE_EPSILON < event.phaseEndsAt) return;

  const nextLap = event.lap <= 0 ? 1 : event.lap + 1;
  const nextTriggerDistance = (nextLap - 1) * race.trackLength + definition.triggerDistance;
  if (leaderDistance(race) + PHASE_EPSILON < nextTriggerDistance) return;
  beginNextLapEvent(race, event, definition, nextLap, now);
}

function kartOccupiesBlockedLane(kart: Pick<KartState, "lateral">, event: CourseEventState, definition: CourseEventDefinition): boolean {
  const blockedCentre = event.blockedLane * definition.laneCenter;
  return Math.abs(kart.lateral - blockedCentre) <= definition.laneWidth * 0.5;
}

function kartIsInsideEvent(kart: Pick<KartState, "distance" | "lateral">, event: CourseEventState, definition: CourseEventDefinition, trackLength: number): boolean {
  const centreDistance = eventCentreDistance(event, definition, trackLength);
  return Math.abs(kart.distance - centreDistance) <= definition.hitDistance
    && kartOccupiesBlockedLane(kart, event, definition);
}

function applyActiveEventHits(race: RaceState, event: CourseEventState, definition: CourseEventDefinition): void {
  if (event.phase !== "active" || event.lap <= 0) return;
  const trackLength = finiteOr(race.trackLength, getTrackLength(race.trackId));
  const slowdown = clamp(definition.slowdownFactor, MIN_SLOWDOWN_FACTOR, MAX_SLOWDOWN_FACTOR);
  for (const kart of race.karts) {
    const cooldown = Math.max(0, finiteOr(kart.courseEventCooldown, 0));
    kart.courseEventCooldown = cooldown;
    if (cooldown > 0 || kart.finishedAt !== null || kart.airborne || kart.ghostTime > 0 || kart.spinTime > 0) continue;
    if (!kartIsInsideEvent(kart, event, definition, trackLength)) continue;

    // Shields absorb a course-event hit exactly as they absorb foam/keg hits;
    // the immunity window still prevents an immediate repeat on the same lane.
    if (kart.shieldTime > 0) kart.shieldTime = 0;
    else {
      kart.velocityX *= slowdown;
      kart.velocityZ *= slowdown;
      kart.speed = Math.hypot(kart.velocityX, kart.velocityZ);
    }
    kart.courseEventCooldown = Math.max(definition.hitCooldown, definition.activeDuration + 0.05);
    kart.courseEventHitCount = Math.max(0, Math.floor(finiteOr(kart.courseEventHitCount, 0))) + 1;
    event.hitCount += 1;
  }
}

/**
 * Advance the authoritative event timeline and apply any active-lane hits.
 * Call once per fixed simulation tick after race.elapsed has advanced.
 */
export function stepCourseEvents(race: RaceState, dt: number): void {
  // The event only runs in a live race.  Countdown/menu/results snapshots keep
  // their last state and cannot accidentally consume a lap while stationary.
  if (race.phase !== "race") return;
  const safeDt = Number.isFinite(dt) && dt > 0 ? dt : 0;
  const definition = getCourseEventDefinition(race.trackId, race.raceVariant);
  for (const kart of race.karts) {
    kart.courseEventCooldown = Math.max(0, finiteOr(kart.courseEventCooldown, 0) - safeDt);
  }
  if (!definition) { race.courseEvent = null; return; }
  const event = ensureCourseEvent(race, definition);
  updateEventState(race, event, definition);
  applyActiveEventHits(race, event, definition);
}

/** Singular alias for callers that step one race event at a time. */
export function stepCourseEvent(race: RaceState, dt: number): void {
  stepCourseEvents(race, dt);
}

export function isCourseEventLaneBlocked(
  lane: CourseEventLane,
  eventOrRace: Pick<CourseEventState, "blockedLane"> | Pick<RaceState, "courseEvent">,
): boolean {
  const event = "courseEvent" in eventOrRace ? eventOrRace.courseEvent : eventOrRace;
  return event !== null && lane === event.blockedLane;
}

export function getCourseEventSafeLane(
  eventOrRace: Pick<CourseEventState, "safeLane"> | Pick<RaceState, "courseEvent">,
): CourseEventLane | null {
  const event = "courseEvent" in eventOrRace ? eventOrRace.courseEvent : eventOrRace;
  return event?.safeLane ?? null;
}

/**
 * Return the safe lane when the kart is close enough to react to a telegraph or
 * active event.  The helper intentionally does not inspect laneBias: the AI
 * caller decides whether an explicit Twitch lane directive has priority.
 */
export function getCourseEventAvoidanceLane(
  kart: Pick<KartState, "distance" | "airborne" | "finishedAt">,
  race: RaceState,
): CourseEventLane | null {
  const event = race.courseEvent;
  if (!event || event.lap <= 0 || (event.phase !== "telegraph" && event.phase !== "active")) return null;
  if (kart.finishedAt !== null || !Number.isFinite(kart.distance)) return null;
  const definition = getCourseEventDefinitionById(event.definitionId || event.id) ?? getCourseEventDefinition(race.trackId, race.raceVariant);
  if (!definition || definition.trackId !== race.trackId) return null;
  const trackLength = finiteOr(race.trackLength, getTrackLength(race.trackId));
  const gap = eventCentreDistance(event, definition, trackLength) - kart.distance;
  const reactionDistance = event.phase === "telegraph"
    ? definition.telegraphDistance + 18
    : definition.hitDistance + 18;
  if (gap < -definition.hitDistance || gap > reactionDistance) return null;
  return event.safeLane;
}

export function getCourseEventSafeLateral(
  kart: Pick<KartState, "distance" | "airborne" | "finishedAt">,
  race: RaceState,
): number | null {
  const lane = getCourseEventAvoidanceLane(kart, race);
  const event = race.courseEvent;
  if (lane === null || !event) return null;
  const definition = getCourseEventDefinitionById(event.definitionId || event.id) ?? getCourseEventDefinition(race.trackId, race.raceVariant);
  return definition && definition.trackId === race.trackId ? lane * definition.laneCenter : null;
}

/** Alias used by AI/renderer integrations that prefer "safe" over "avoidance". */
export const getSafeCourseEventLane = getCourseEventAvoidanceLane;
